/**
 * BrickBrackets legal pages (/privacy, /terms) and the footer attribution, as
 * the little Markdown the shell renders (headings, paragraphs, "- " lists,
 * **bold**, [links](https://…)). English here; Spanish in ./locales/es-MX.ts
 * (`legal`). Keep both in step and bump "Last updated" when either changes.
 *
 * DRAFT written from what the code does (sign-in, stored data, analytics,
 * Rebrickable): have it reviewed before relying on it.
 */

export const operator = "BrickBrackets.io";
export const contactEmail = "hello@brickbrackets.io";

export const attribution = "Set numbers, names and pictures from [Rebrickable](https://rebrickable.com).";

export const privacy = `# Privacy

Last updated: October 18, 2026

BrickBrackets.io ("we") lets you size 3D-printable display brackets for LEGO® sets. This page says what we keep about you and why. Questions: [${contactEmail}](mailto:${contactEmail}).

## Without an account

- **Your brackets are built in your browser.** Changing dimensions sends nothing to us.
- **Anonymous usage counts.** We record which designs are viewed, configured, downloaded, saved or shared (design and version, never who). They help us decide what to design next.
- **Kept in your browser only:** your language, brackets you save "on this device", and a list of what you downloaded so we can ask later whether it fit. Clear your browser data to remove them.

## With an account

You sign in with Google. We then keep:

- **From Google:** your name, email address and profile picture. Never shown to other people.
- **Your public name**, which you choose. It appears next to the sets you tag and the prints you register.
- **Your saved brackets:** private, only you see them.
- **Your tags and prints:** public, under your public name.
- **Your favorites and reports:** private (moderators see reports).
- **Downloads:** we count one download per person per design, so the counts on the site are real.

## Who else is involved

- **Amazon Web Services** hosts the site and stores the data above.
- **Google** handles sign-in.
- **Rebrickable** provides set data. When you are signed in, what you type in a set field is sent from our server to Rebrickable to search sets; set pictures load from Rebrickable's servers in your browser.

We don't sell your data, show ads or use advertising cookies. The only cookie-like storage is your sign-in session and the browser storage listed above.

## Your choices

- Delete saved brackets and remove your tags at any time from the site.
- To get a copy of your data or delete your account, write to [${contactEmail}](mailto:${contactEmail}). We answer within 30 days.
`;

export const terms = `# Terms of use

Last updated: October 18, 2026

BrickBrackets.io is free to use. By using it you agree to these terms. Questions: [${contactEmail}](mailto:${contactEmail}).

## The brackets

- Brackets are generated from the dimensions you choose. **Check the print and the fit before you rely on it**: we can't promise a bracket fits your set, prints well on your printer or holds a particular weight.
- You are responsible for printing, mounting (wall plugs, screws, pegboards) and for what you display on it.
- Files you download are for your own use. Don't sell them or offer them for download elsewhere as your own.

## The community

- Tags and prints you add are public, under your public name. Tag only sets you believe the dimensions fit, and register prints you actually made.
- Be respectful. Reports from other people can hide a tag until a moderator reviews it; we may remove tags, names or accounts that are misleading, spam or offensive.

## The service

- The site is provided as it is, without warranties. We may change or stop features, designs or the site.
- To the extent the law allows, we aren't liable for damage caused by a printed bracket or by using the site.

## Trademarks

LEGO® is a trademark of the LEGO Group, which does not sponsor, authorize or endorse this site. Set names and numbers identify the sets a bracket fits. IKEA and SKÅDIS are trademarks of Inter IKEA Systems B.V.
`;

export const attributionEs = "Números, nombres e imágenes de sets de [Rebrickable](https://rebrickable.com).";

export const privacyEs = `# Privacidad

Última actualización: 18 de octubre de 2026

BrickBrackets.io ("nosotros") te permite ajustar soportes imprimibles en 3D para exhibir sets de LEGO®. Esta página explica qué guardamos sobre ti y por qué. Dudas: [${contactEmail}](mailto:${contactEmail}).

## Sin cuenta

- **Tus soportes se generan en tu navegador.** Cambiar medidas no nos envía nada.
- **Conteos de uso anónimos.** Registramos qué diseños se ven, configuran, descargan, guardan o comparten (diseño y versión, nunca quién). Nos ayudan a decidir qué diseñar después.
- **Solo en tu navegador:** tu idioma, los soportes que guardas "en este dispositivo" y una lista de lo que descargaste para preguntarte después si le quedó. Borra los datos del navegador para eliminarlos.

## Con cuenta

Inicias sesión con Google. Entonces guardamos:

- **De Google:** tu nombre, correo y foto de perfil. Nunca se muestran a otras personas.
- **Tu nombre público**, que tú eliges. Aparece junto a los sets que etiquetas y las impresiones que registras.
- **Tus soportes guardados:** privados, solo tú los ves.
- **Tus etiquetas e impresiones:** públicas, bajo tu nombre público.
- **Tus favoritos y reportes:** privados (los moderadores ven los reportes).
- **Descargas:** contamos una descarga por persona y diseño, para que los conteos del sitio sean reales.

## Quién más participa

- **Amazon Web Services** aloja el sitio y guarda los datos anteriores.
- **Google** gestiona el inicio de sesión.
- **Rebrickable** aporta los datos de los sets. Con sesión iniciada, lo que escribes en un campo de set se envía desde nuestro servidor a Rebrickable para buscar sets; las imágenes de los sets se cargan desde los servidores de Rebrickable en tu navegador.

No vendemos tus datos, no mostramos anuncios ni usamos cookies publicitarias. Lo único parecido a cookies es tu sesión y el almacenamiento del navegador descrito arriba.

## Tus opciones

- Borra tus soportes guardados y quita tus etiquetas cuando quieras desde el sitio.
- Para obtener una copia de tus datos o eliminar tu cuenta, escribe a [${contactEmail}](mailto:${contactEmail}). Respondemos en un plazo de 30 días.
`;

export const termsEs = `# Términos de uso

Última actualización: 18 de octubre de 2026

BrickBrackets.io es gratuito. Al usarlo aceptas estos términos. Dudas: [${contactEmail}](mailto:${contactEmail}).

## Los soportes

- Los soportes se generan con las medidas que eliges. **Revisa la impresión y el ajuste antes de confiar en ellos**: no podemos prometer que un soporte le quede a tu set, se imprima bien en tu impresora o aguante cierto peso.
- Tú eres responsable de imprimir, montar (taquetes, tornillos, tableros) y de lo que exhibas en él.
- Los archivos que descargas son para tu propio uso. No los vendas ni los ofrezcas para descarga en otro lugar como propios.

## La comunidad

- Las etiquetas e impresiones que agregas son públicas, bajo tu nombre público. Etiqueta solo sets a los que creas que les quedan las medidas, y registra impresiones que de verdad hiciste.
- Sé respetuoso. Los reportes de otras personas pueden ocultar una etiqueta hasta que un moderador la revise; podemos quitar etiquetas, nombres o cuentas engañosas, spam u ofensivas.

## El servicio

- El sitio se ofrece tal cual, sin garantías. Podemos cambiar o dejar de ofrecer funciones, diseños o el sitio.
- En la medida que la ley lo permita, no somos responsables de daños causados por un soporte impreso o por el uso del sitio.

## Marcas

LEGO® es una marca comercial del Grupo LEGO, el cual no patrocina, autoriza ni respalda este sitio. Los nombres y números de sets identifican los sets a los que les queda un soporte. IKEA y SKÅDIS son marcas de Inter IKEA Systems B.V.
`;
